import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  addDetectiveMembers, getDetectiveConfig, listDetectiveMembers,
  removeDetectiveMember, retryDetectiveMember, startDetectiveMonitoring,
} from '../../../services/detectiveApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalSectionHeader, PortalErrorState,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

// Must match `color` in engines/detective.js — every Detective route claims the
// same indigo.
const DETECTIVE_COLOR = '#3949AB';
const DETECTIVE_COLOR_DARK = '#283593';

const detectiveButtonSx = {
  backgroundColor: DETECTIVE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: DETECTIVE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const rowButtonSx = { fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' };

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const noteTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.body };
const errorTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.red };

const enrollmentHeader = (
  <PortalPageHeader
    icon={<GroupsIcon sx={{ fontSize: 24 }} />}
    title="Enrollment"
    subtitle="Manage which accounts are members of the Amazon Detective behavior graph"
    color={DETECTIVE_COLOR}
  />
);

// PRISM's phase vocabulary — DetectiveMemberStatus.Phase. One of two columns
// (DD8); it says how far Prism got, never what AWS thinks.
//
// "disabled" is labelled "Removed", never "Disabled". The row is a TOMBSTONE and
// also the grant-detach ledger: it is written after the account was taken out of
// the graph and Prism's CKPrism-Detective-Permissions policy was detached from
// its switch role. "Disabled" has repeatedly been read as "present but switched
// off", which is the opposite of what the row records.
const PHASE_META = {
  pending: { intent: 'neutral', label: 'Pending' },
  granting: { intent: 'info', label: 'Granting access' },
  enabling: { intent: 'info', label: 'Enabling' },
  inviting: { intent: 'info', label: 'Inviting' },
  accepting: { intent: 'info', label: 'Accepting invitation' },
  enabled: { intent: 'success', label: 'Enabled' },
  failed: { intent: 'error', label: 'Failed' },
  removing: { intent: 'warning', label: 'Removing' },
  disabled: { intent: 'neutral', label: 'Removed' },
};

const phaseMeta = (p) => PHASE_META[p] || { intent: 'neutral', label: formatStatusLabel(p) };

// AWS's vocabulary — MemberDetail.Status, stored verbatim. This is the SECOND
// column DD8 buys, and the enrollment page is the only screen that can show the
// operator what AWS actually answered.
//
// Only two entries get a Prism-authored label, and both because AWS's own word
// is actively misleading on this screen:
//
//   VERIFICATION_FAILED    — reads like a Prism failure; it means AWS checked the
//                            address against the account's real root user and
//                            rejected it. The remedy is a corrected address.
//   ACCEPTED_BUT_DISABLED  — reads like a removal; it means the account DID join
//                            the graph and Detective is holding it out of
//                            ingestion. The remedy is StartMonitoringMember.
//
// The rest are AWS's statuses Title-Cased and nothing else, and every chip
// carries the raw token in its tooltip so the exact value is never more than a
// hover away.
const MEMBER_STATUS_META = {
  ENABLED: { intent: 'success', label: 'Enabled' },
  INVITED: { intent: 'neutral', label: 'Invited' },
  VERIFICATION_IN_PROGRESS: { intent: 'neutral', label: 'Verification in progress' },
  VERIFICATION_FAILED: { intent: 'error', label: 'Email not verified' },
  ACCEPTED_BUT_DISABLED: { intent: 'warning', label: 'Accepted, not ingesting' },
};

const memberStatusMeta = (s) => MEMBER_STATUS_META[s]
  || { intent: 'neutral', label: formatStatusLabel(s) };

// Every non-terminal phase Detective can write. 'granting' and 'inviting' are the
// two GuardDuty's page omits, and omitting either freezes the bar: the grant gate
// assumes a role in every enrolled account and waits for IAM propagation, and the
// invitation/verification wait runs to Detective's 8-minute verify deadline.
const ACTIVE_PHASES = ['pending', 'granting', 'enabling', 'inviting', 'accepting', 'removing'];

// The AWS statuses that mean "this account is IN the behavior graph but has not
// finished joining". A failed cell in any of them is holding one of the graph's
// 1,200 member slots — see FAILED_SLOT_NOTE.
const IN_GRAPH_UNSETTLED = ['INVITED', 'VERIFICATION_IN_PROGRESS', 'VERIFICATION_FAILED'];

// A failed cell is not a no-op AWS-side. The account is IN the graph and holds a
// slot; the fix is to retry it or REMOVE it, and doing neither silently consumes
// quota that Detective's synchronous pre-flight will later refuse an enable over —
// naming a limit the operator has no idea they are already spending.
//
// ListMembers "include[s]… member accounts that have not yet accepted the
// invitation" and DeleteMembers has no member-status precondition, so an
// un-accepted or verification-failed account is a member either way.
const FAILED_SLOT_NOTE = 'This account still occupies one of the behavior graph\'s 1,200 member slots. '
  + 'Retry it, or remove it from Detective to release the slot.';

// The ACCEPTED_BUT_DISABLED copy, selected by MemberDisabledReason. That column is
// populated ONLY alongside this status — it is empty on every other failing state,
// verified against a real VERIFICATION_FAILED member — so nothing here may treat
// it as a general "reason" field.
const disabledReasonNote = (reason) => {
  if (reason === 'VOLUME_UNKNOWN') {
    return 'Amazon Detective accepted this account into the behavior graph but is not ingesting its '
      + 'data, and could not determine the account\'s data volume. The usual cause is that the account '
      + 'is not enrolled in Amazon GuardDuty. Enroll it there, then start monitoring.';
  }
  if (reason === 'VOLUME_TOO_HIGH') {
    return 'Amazon Detective accepted this account into the behavior graph but is not ingesting its '
      + 'data: its volume is above Detective\'s 10 TB per day ingest limit. Start monitoring will only '
      + 'succeed once the volume drops below that limit — pressing it sooner is refused by AWS.';
  }
  return 'Amazon Detective accepted this account into the behavior graph but is not ingesting its '
    + 'data. AWS did not report which of its two ingest limits is holding it out.';
};

// DetectiveConfigResponse.linkingRegions is a decoded []string, but tolerate a raw
// JSON string so the page never blanks if the DTO shape shifts.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

// Membership mutations are refused by the backend unless the config is settled, so
// every button that dispatches one is gated on the same condition or it produces a
// guaranteed 400.
const configAllowsMemberOps = (status) => status === 'enabled' || status === 'partial_failure';

// The one chip that summarises an account whose regions disagree. "failed" wins
// because it is the only phase that carries an action.
const summaryPhase = (phases) => {
  if (phases.includes('failed')) return 'failed';
  for (const p of ['removing', 'granting', 'inviting', 'accepting', 'enabling', 'pending']) {
    if (phases.includes(p)) return p;
  }
  if (phases.length > 0 && phases.every((p) => p === 'disabled')) return 'disabled';
  return 'enabled';
};

const CLOSED_EMAIL_DIALOG = {
  open: false, accountIds: [], rows: [], manual: {}, message: '',
};

// One label/value row of the configuration card.
function ConfigRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 170, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

// One region's cell: Prism's phase and AWS's member status, side by side.
//
// The AWS chip is rendered ALWAYS, not only when the two disagree. The whole
// point of carrying two columns is that the operator can see AWS's answer;
// showing it only on a mismatch means the one screen that could explain a stuck
// enrollment hides the evidence until it is too late to be useful.
//
// `showMemberStatus` is false for exactly one row — the administrator's. AWS
// reports the administrator as a member of its own graph, so Tasks 12 and 13 skip
// it when reading ListMembers rather than writing a member status onto the account
// that OWNS the graph. Its MemberStatus is therefore always empty, and a blank
// chip in the column every other row fills reads as a bug.
function CellChips({ cell, showMemberStatus }) {
  const phase = phaseMeta(cell.phase);
  const status = cell.memberStatus ? memberStatusMeta(cell.memberStatus) : null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      <Chip size="small" label={`${cell.region} · ${phase.label}`} sx={statusChipSx(phase.intent)} />
      {showMemberStatus && status && (
        <Chip
          size="small"
          label={status.label}
          title={`AWS member status: ${cell.memberStatus}`}
          sx={statusChipSx(status.intent)}
        />
      )}
      {showMemberStatus && !status && (
        <Chip
          size="small"
          label="No AWS status yet"
          title="AWS has not reported a member status for this account/region yet"
          sx={statusChipSx('neutral')}
        />
      )}
    </Stack>
  );
}

export default function DetectiveEnrollmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // Three independent failures, three independent messages. Collapsing them into
  // one `error` is how a failed members call ends up rendering as "no accounts
  // enrolled yet" — a reassuring lie about the one table that records whether an
  // account still carries Prism's IAM policy.
  const [configError, setConfigError] = useState(null);
  const [membersError, setMembersError] = useState(null);
  const [accountsError, setAccountsError] = useState(null);
  // Action-level failures (Add / Retry / Remove / Start monitoring).
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [busy, setBusy] = useState(false);
  // accountId -> the address typed into a VERIFICATION_FAILED row's inline field.
  const [retryEmails, setRetryEmails] = useState({});
  const [emailDialog, setEmailDialog] = useState(CLOSED_EMAIL_DIALOG);
  const pollRef = useRef(null);

  // showSpinner is false for post-mutation refreshes: swapping the whole page for
  // a loading state after every Add/Remove/Retry throws away the table the user is
  // watching, and the async work has only just been dispatched anyway.
  //
  // allSettled, never all: a rejected accounts call must not take the members list
  // down with it, and a rejected members call must not be reported as a missing
  // configuration.
  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    const [cfgRes, memRes, acctRes] = await Promise.allSettled([
      getDetectiveConfig(), listDetectiveMembers(), listSecurityHubAccounts(),
    ]);

    if (cfgRes.status === 'fulfilled') {
      // A tenant that has never enabled Detective resolves with data: null. That
      // is the setup signal, not a fault — only a rejection is an error.
      setConfig(cfgRes.value?.data || null);
      setConfigError(null);
    } else {
      // The previous config is deliberately KEPT. A stale view with a visible
      // error is honest; replacing a configured tenant's page with the setup
      // prompt is not.
      setConfigError(cfgRes.reason?.message || 'Could not load the Amazon Detective configuration');
    }

    if (memRes.status === 'fulfilled') {
      const rows = memRes.value?.data || [];
      setMembers(rows);
      setMembersError(null);
      // A poll can enroll an account that is still ticked in the picker below —
      // most obviously the ones this page just submitted. Re-sending an enrolled
      // account is skipped server-side, and if EVERY tick is already enrolled the
      // whole request is refused, which reads as a failure of an Add that in fact
      // succeeded.
      const live = new Set(rows.filter((r) => r.phase !== 'disabled').map((r) => r.accountId));
      setSelected((prev) => ([...prev].some((id) => live.has(id))
        ? new Set([...prev].filter((id) => !live.has(id)))
        : prev));
    } else {
      setMembersError(memRes.reason?.message || 'Could not load the enrolled accounts');
    }

    if (acctRes.status === 'fulfilled') {
      // No `|| a?.data` fallback: the envelope's data IS an object, so falling
      // back to it would hand an object to the .filter() below instead of `[]`.
      setAccounts(acctRes.value?.data?.accounts || []);
      setAccountsError(null);
    } else {
      setAccountsError(acctRes.reason?.message || 'Could not load the organization\'s accounts');
    }

    if (showSpinner) setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while EITHER a cell is mid-flight OR the config is mid-operation. The
  // config half is the second half of the check, not a redundancy: a config in
  // 'in_progress' whose cells have all settled still has work running (the
  // verification poll, the region loop), and polling only on cell phases stops one
  // tick too early.
  //
  // IMPORTANT: this effect deliberately does NOT clearInterval in its cleanup. The
  // tick calls setMembers(next), which produces a new array reference every time,
  // so [members] changes and React fires the cleanup — killing the interval and
  // restarting its countdown on every tick. That destroy-and-recreate cycle is
  // what freezes the progress bar. The interval stops itself when no active phase
  // remains (the inner check), and the unmount effect below is the real cleanup.
  useEffect(() => {
    const isActive = members.some((m) => ACTIVE_PHASES.includes(m.phase))
      || config?.status === 'in_progress';
    if (isActive && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const [m, c] = await Promise.all([listDetectiveMembers(), getDetectiveConfig()]);
          const next = m?.data || [];
          const nextCfg = c?.data || null;
          setMembers(next);
          setMembersError(null);
          setConfig(nextCfg);
          setConfigError(null);
          const stillActive = nextCfg?.status === 'in_progress'
            || next.some((r) => ACTIVE_PHASES.includes(r.phase));
          if (!stillActive && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (_) {
          // Transient: the next tick retries. Deliberately does NOT clear the
          // existing error banners — a blipped poll is not evidence that an
          // earlier, reported failure has been repaired.
        }
      }, POLL_INTERVAL_MS);
    }
  }, [members, config?.status]);

  useEffect(() => () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading enrollment…" color={DETECTIVE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  // MUST precede the not-configured branch. A rejection from GET /config leaves
  // `config` null, so without this a 500 renders as "Amazon Detective is not set up
  // yet" and points the operator at the setup wizard for a capability that is
  // configured and running — while the real message, already captured, is
  // discarded with the rest of the page.
  if (configError && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={configError} onRetry={() => load()} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/detective')}>
              Go to setup
            </Button>
          }>
            Amazon Detective is not set up yet for this customer. Complete the setup
            wizard first — it creates the behavior graph that member accounts are
            invited into.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  // One row per (account, region) collapses to one row per account, because every
  // action the API offers — retry, start monitoring, remove — is account-scoped.
  // The per-region truth is not thrown away: each cell is rendered as its own pair
  // of chips inside the row.
  //
  // Every row here comes from GET /detective/members, which answers for THIS
  // tenant's single config and nothing else. Rows are keyed by accountId within
  // that response and the array is replaced wholesale on every load, so no member
  // state can survive a config change or leak between tenants.
  const byAccount = new Map();
  for (const m of members) {
    const prev = byAccount.get(m.accountId) || {
      accountId: m.accountId, cells: [], phases: [], errors: [], retries: 0,
    };
    prev.cells.push(m);
    prev.phases.push(m.phase);
    if (m.errorMessage) prev.errors.push(`${m.region}: ${m.errorMessage}`);
    prev.retries = Math.max(prev.retries, m.retryCount || 0);
    byAccount.set(m.accountId, prev);
  }

  const allRows = [...byAccount.values()];
  const enrolledAccounts = allRows.filter((row) => summaryPhase(row.phases) !== 'disabled');
  // Accounts whose every cell is a tombstone. They are NOT dropped from the page.
  // The row is the only record that Prism attached CKPrism-Detective-Permissions to
  // that account's switch role and then detached it; an account that vanishes from
  // the UI when it should read as "Removed" is an account nobody will think to
  // check. They are listed in their own section AND offered back in the picker
  // below, which is the path that re-enrolls one.
  const removedAccounts = allRows.filter((row) => summaryPhase(row.phases) === 'disabled');
  const removedIds = new Set(removedAccounts.map((r) => r.accountId));
  const enrolledIds = new Set(enrolledAccounts.map((r) => r.accountId));
  const notEnrolled = accounts.filter((a) => !enrolledIds.has(a.accountId));

  // GET /securityhub/accounts returns {accounts:[{accountId, name, status}]} —
  // `name` is the only name field there is. One label helper serves every table so
  // an account cannot read differently depending on which one it is sitting in.
  const nameById = {};
  accounts.forEach((a) => { if (a.accountId) nameById[a.accountId] = a.name || ''; });
  const acctLabel = (id) => (nameById[id] ? `${nameById[id]} (${id})` : id);

  const linkingRegions = toStringArray(config.linkingRegions);
  const canMutate = configAllowsMemberOps(config.status);
  // "failed" is terminal: no fan-out is running, and every mutation gate refuses it
  // until setup is re-run. It must never share the "waiting for the current
  // operation" copy, which promises a completion that will never arrive.
  const setupFailed = config.status === 'failed';
  const operationRunning = config.status === 'in_progress'
    || members.some((m) => ACTIVE_PHASES.includes(m.phase));

  const run = async (fn) => {
    setBusy(true);
    let failure = null;
    try {
      await fn();
    } catch (e) {
      failure = e.message || 'The request failed';
    }
    // Mutations answer with the operation claimed and the real work dispatched
    // asynchronously, so the response says nothing about the outcome. Every path
    // re-reads the members list, which is the only source of a row's true state.
    await load(false).catch(() => {});
    // AFTER the reload, never before: a successful load() clears its own errors, so
    // a failure set first would be wiped a few milliseconds later and the rejected
    // mutation would look like it went through.
    setError(failure);
    setBusy(false);
  };

  // Add is a two-pass flow, and the second pass is a step rather than a failure.
  // POST /detective/members answers 422 with `missing[]` — the accounts whose AWS
  // root user email could not be resolved, or was resolved and refused — and
  // detectiveApi rejects with that array attached. Sending the operator back with
  // exactly those accounts pre-filled is the only way a correction can be made; a
  // generic "Request failed" about accounts they cannot identify is a dead end.
  const submitAdd = async (accountIds, memberEmails) => {
    setBusy(true);
    let failure = null;
    try {
      await addDetectiveMembers({ accountIds, memberEmails });
      setSelected(new Set());
      setEmailDialog(CLOSED_EMAIL_DIALOG);
    } catch (e) {
      if (e.missingEmails?.length) {
        setEmailDialog((prev) => ({
          open: true,
          accountIds,
          rows: e.missingEmails,
          // Pre-filled with whatever address was resolved and refused, so the
          // operator edits rather than retypes. Rows with no address start empty.
          //
          // A value already TYPED wins over the server's, because a second 422 on
          // the same account is the normal shape of a correction that is still
          // wrong — and rebuilding the map from the response would wipe the
          // operator's work every time they got it wrong once.
          manual: e.missingEmails.reduce((acc, row) => {
            const typed = (prev.manual[row.accountId] || '').trim();
            acc[row.accountId] = typed || row.email || '';
            return acc;
          }, {}),
          // e.message IS the server's sentence, or detectiveApi's byte-identical
          // fallback. No third variant is written here.
          message: e.message,
        }));
      } else {
        failure = e.message || 'Could not add the selected accounts';
      }
    }
    await load(false).catch(() => {});
    setError(failure);
    setBusy(false);
  };

  const onAdd = () => submitAdd([...selected], {});

  // Close the dialog before dispatching, not after it succeeds: a rejection is
  // reported in the page-level banner, which an open modal would sit on top of.
  const onRemove = (accountId) => {
    setConfirmRemove(null);
    return run(() => removeDetectiveMember(accountId));
  };

  // `email` is optional. It is supplied only from a VERIFICATION_FAILED row's
  // inline field: that status means resolution SUCCEEDED and AWS rejected the
  // result, so retrying with the same address fails identically. The backend uses a
  // supplied value verbatim rather than re-resolving, which would return the same
  // rejected address and loop forever.
  //
  // Deliberately one account at a time, with no "retry all": a retry claims the
  // config before it returns, and the next one is then refused — so a loop over the
  // failed accounts would retry the first and report a spurious error for the rest.
  const onRetry = (accountId, email) => run(async () => {
    await retryDetectiveMember(accountId, email);
    setRetryEmails((prev) => {
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  });

  const onStartMonitoring = (accountId) => run(() => startDetectiveMonitoring(accountId));

  const emailDialogComplete = emailDialog.rows.length > 0
    && emailDialog.rows.every((row) => (emailDialog.manual[row.accountId] || '').trim() !== '');

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        </motion.div>
      )}
      {configError && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The configuration below could not be refreshed and may be out of date: {configError}
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Configuration"
          subtitle="One account owns the behavior graph; every other account joins it by invitation"
          color={DETECTIVE_COLOR}
        />
        <ConfigRow label="Administrator account" value={acctLabel(config.adminAccountId)} />
        <ConfigRow label="Home region" value={config.homeRegion} />
        <ConfigRow label="Regions" value={linkingRegions.join(', ')} />
        <ConfigRow label="Status" value={formatStatusLabel(config.status)} />
      </Paper>
      </motion.div>

      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/detective')}>
                Re-run setup
              </Button>
            )}
          >
            Amazon Detective setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own — enrollment
            changes stay unavailable until setup is re-run. The accounts below show
            how far each one got, and any account listed as a graph member is still
            holding one of its 1,200 slots.
          </Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'Working…'}
            sx={{ mb: 3 }}
          />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          title="Graph members"
          subtitle="Prism's phase and the status AWS reports, per account and region"
          color={DETECTIVE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${enrolledAccounts.length} enrolled`}
              sx={statusChipSx('info')}
            />
          )}
        />
        {membersError && (
          <PortalErrorState error={membersError} onRetry={() => load()} sx={{ mb: 2 }} />
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Regions and AWS status</TableCell>
                <TableCell sx={headCellSx}>Progress</TableCell>
                <TableCell align="right" sx={headCellSx}>Retries</TableCell>
                <TableCell align="right" sx={headCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrolledAccounts.map((row, index) => {
                const phase = summaryPhase(row.phases);
                const meta = phaseMeta(phase);
                const isAdmin = row.accountId === config.adminAccountId;

                // Every Detective-specific action is driven by memberStatus, not by
                // phase: phase is "failed" for all three of the states below and
                // cannot tell them apart (DD8).
                const verificationFailedCell = row.cells.find(
                  (c) => c.memberStatus === 'VERIFICATION_FAILED',
                );
                const monitorCells = row.cells.filter(
                  (c) => c.memberStatus === 'ACCEPTED_BUT_DISABLED',
                );
                // Retry is offered for failures where the account is NOT in the
                // graph. An ACCEPTED_BUT_DISABLED cell is excluded on purpose: that
                // account is already a member, CreateMembers returns it as already
                // present and AcceptInvitation 409s, so a Retry there is a button
                // that can never work.
                const retryableCells = row.cells.filter(
                  (c) => c.phase === 'failed' && c.memberStatus !== 'ACCEPTED_BUT_DISABLED',
                );
                // The inline email field is only ever shown alongside a live Retry
                // button. A field with nothing to submit it is worse than no field:
                // it invites a correction that goes nowhere.
                const showRetry = retryableCells.length > 0;
                const askForEmail = showRetry && !!verificationFailedCell;
                const holdsSlot = row.cells.some(
                  (c) => c.phase === 'failed' && IN_GRAPH_UNSETTLED.includes(c.memberStatus),
                );
                const typedEmail = retryEmails[row.accountId] !== undefined
                  ? retryEmails[row.accountId]
                  : (verificationFailedCell?.email || '');

                return (
                  <TableRow key={row.accountId} hover sx={rowSx(index)}>
                    <TableCell sx={bodyCellSx}>
                      {acctLabel(row.accountId)}
                      {isAdmin && (
                        <Chip
                          size="small"
                          label="Administrator"
                          sx={{ ml: 1, ...statusChipSx('info') }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack spacing={0.5}>
                        {row.cells.map((c) => (
                          <CellChips
                            key={`${c.accountId}-${c.region}`}
                            cell={c}
                            showMemberStatus={!isAdmin}
                          />
                        ))}
                      </Stack>
                      {isAdmin && (
                        <Typography sx={{ ...noteTextSx, color: CK.muted, mt: 0.5 }}>
                          The administrator owns the behavior graph. AWS reports it as a
                          member of its own graph, so Prism does not record a member
                          status for it.
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={meta.label} size="small" sx={statusChipSx(meta.intent)} />
                        {ACTIVE_PHASES.includes(phase) && (
                          <CircularProgress size={14} thickness={5} sx={{ color: DETECTIVE_COLOR }} />
                        )}
                      </Stack>
                      {row.errors.length > 0 && (
                        <Typography sx={{ ...errorTextSx, display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
                          {row.errors.join(' • ')}
                        </Typography>
                      )}
                      {verificationFailedCell && (
                        <Typography sx={{ ...errorTextSx, display: 'block', mt: 0.5 }}>
                          AWS could not verify {verificationFailedCell.email || 'the address supplied'} against
                          this account&rsquo;s real AWS root user. Retrying with the same address fails
                          identically
                          {/* The instruction is dropped when no field is rendered. This message is
                              gated on verificationFailedCell but the field on askForEmail, which also
                              requires a retryable cell — so a VERIFICATION_FAILED cell whose phase has
                              not yet been stamped 'failed' would otherwise say "correct it below
                              first" with nothing below it. The EXPLANATION must survive that gap: it
                              is the only thing on the row that says why the account failed. */}
                          {askForEmail ? ' — correct it below first.' : '.'}
                        </Typography>
                      )}
                      {monitorCells.map((c) => (
                        <Typography
                          key={`${c.region}-disabled-reason`}
                          sx={{ ...noteTextSx, color: CK.amber, display: 'block', mt: 0.5 }}
                        >
                          {c.region}: {disabledReasonNote(c.disabledReason)}
                        </Typography>
                      ))}
                      {holdsSlot && (
                        <Typography sx={{ ...noteTextSx, color: CK.amber, display: 'block', mt: 0.5 }}>
                          {FAILED_SLOT_NOTE}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {row.retries > 0 ? row.retries : '—'}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {isAdmin ? (
                        <Typography sx={{ ...noteTextSx, color: CK.muted }}>
                          Removing the administrator is a full disable — use Settings.
                        </Typography>
                      ) : (
                        <Stack spacing={1} alignItems="flex-end">
                          {askForEmail && (
                            <TextField
                              size="small"
                              label="AWS root user email address"
                              value={typedEmail}
                              onChange={(e) => setRetryEmails(
                                (prev) => ({ ...prev, [row.accountId]: e.target.value }),
                              )}
                              helperText="Must be the account's real AWS root user address."
                              sx={{ minWidth: 260 }}
                            />
                          )}
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {showRetry && (
                              <Button
                                size="small"
                                color="warning"
                                disabled={busy || !canMutate
                                  || (askForEmail && typedEmail.trim() === '')}
                                onClick={() => onRetry(
                                  row.accountId,
                                  askForEmail ? typedEmail.trim() : undefined,
                                )}
                                sx={rowButtonSx}
                              >
                                Retry
                              </Button>
                            )}
                            {monitorCells.length > 0 && (
                              <Button
                                size="small"
                                color="warning"
                                disabled={busy || !canMutate}
                                onClick={() => onStartMonitoring(row.accountId)}
                                sx={rowButtonSx}
                              >
                                Start monitoring
                              </Button>
                            )}
                            {phase !== 'removing' && (
                              <Button
                                size="small"
                                color="error"
                                disabled={busy || !canMutate}
                                onClick={() => setConfirmRemove(row.accountId)}
                                sx={rowButtonSx}
                              >
                                Remove
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {enrolledAccounts.length === 0 && !membersError && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                      title="No accounts in the behavior graph yet"
                      description="Pick accounts from the list below and invite them into the graph."
                      color={DETECTIVE_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      {removedAccounts.length > 0 && (
        <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <PortalSectionHeader
            icon={<DeleteOutlineIcon sx={{ fontSize: 20 }} />}
            title="Removed accounts"
            subtitle="Tombstoned rows — the record that Prism removed each account and detached its scoped IAM policy"
            color={DETECTIVE_COLOR}
            action={(
              <Chip size="small" label={`${removedAccounts.length} removed`} sx={statusChipSx('neutral')} />
            )}
          />
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            These rows are kept, never deleted. Each one is the only record that
            Prism attached its <b>CKPrism-Detective-Permissions</b> policy to that
            account&rsquo;s access role and then detached it — an account that
            disappeared from this page instead would be one nobody could check. Add
            an account back from the picker below to re-invite it.
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell sx={headCellSx}>Regions and last AWS status</TableCell>
                  <TableCell sx={headCellSx}>Last error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {removedAccounts.map((row, index) => (
                  <TableRow key={row.accountId} hover sx={rowSx(index)}>
                    <TableCell sx={bodyCellSx}>{acctLabel(row.accountId)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack spacing={0.5}>
                        {row.cells.map((c) => (
                          <CellChips
                            key={`${c.accountId}-${c.region}`}
                            cell={c}
                            showMemberStatus={row.accountId !== config.adminAccountId}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: CK.muted }}>
                      {row.errors.length > 0 ? row.errors.join(' • ') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<GroupAddIcon sx={{ fontSize: 20 }} />}
          title="Not in the graph"
          subtitle="Linked accounts Amazon Detective is not investigating"
          color={DETECTIVE_COLOR}
          action={(
            <Button
              variant="contained"
              disabled={busy || !canMutate || selected.size === 0}
              onClick={onAdd}
              sx={detectiveButtonSx}
            >
              {selected.size === 0 ? 'Add to Detective' : `Add ${selected.size} to Detective`}
            </Button>
          )}
        />
        <Typography sx={{ ...bodyTextSx, mb: 2 }}>
          Detective enrolls by invitation, and AWS checks each account&rsquo;s AWS root
          user email address before it joins. Prism looks the addresses up
          automatically; if any cannot be resolved, or AWS refuses one, you will be
          asked for exactly those before the invitation is sent.
        </Typography>
        {/* Suppressed when setup failed — the terminal banner above already
            explains it, and this copy would contradict it. What is left is
            in_progress, which the wording is then accurate for. */}
        {!canMutate && !setupFailed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enrollment changes are paused while the current operation finishes.
          </Alert>
        )}
        {accountsError && (
          <PortalErrorState error={accountsError} onRetry={() => load()} sx={{ mb: 2 }} />
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={notEnrolled.some((a) => selected.has(a.accountId)) && !notEnrolled.every((a) => selected.has(a.accountId))}
                    checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                    disabled={notEnrolled.length === 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(notEnrolled.map((a) => a.accountId)) : new Set())}
                  />
                </TableCell>
                <TableCell sx={headCellSx}>Account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notEnrolled.map((a, index) => (
                <TableRow key={a.accountId} hover sx={rowSx(index)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(a.accountId)}
                      onChange={(e) => {
                        const n = new Set(selected);
                        if (e.target.checked) n.add(a.accountId);
                        else n.delete(a.accountId);
                        setSelected(n);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {acctLabel(a.accountId)}
                    {removedIds.has(a.accountId) && (
                      <Chip
                        label="previously removed"
                        size="small"
                        sx={{ ml: 1, ...statusChipSx('neutral') }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {/* The empty state is suppressed when the account list failed to
                  load. "Every linked account is enrolled" is a claim about a list
                  we do not have, and it is the exact shape of reassurance that
                  hides a broken page. */}
              {notEnrolled.length === 0 && !accountsError && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 28 }} />}
                      title="Every linked account is in the graph"
                      description="Nothing left to add — new accounts appear here once they join the organization."
                      color={DETECTIVE_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Remove account from Detective</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Remove <b>{confirmRemove}</b>? It is deleted from the behavior graph in
            every region, which releases the member slot it holds, its cached
            investigations are deleted here, and Prism detaches the IAM policy it
            added to the account&rsquo;s access role. The row stays on this page as a
            record of that. Adding the account back later re-invites it, and AWS
            verifies its root user email address again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmRemove(null)}
            sx={{ ...rowButtonSx, color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => onRemove(confirmRemove)}
            disabled={busy}
            sx={rowButtonSx}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emailDialog.open}
        onClose={() => setEmailDialog(CLOSED_EMAIL_DIALOG)}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>AWS root user email addresses</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {emailDialog.message
              || 'Some member accounts need an AWS root user email address before enrollment'}
          </Alert>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            AWS checks each address against the account&rsquo;s real AWS root user. It
            must be that address and not the account owner&rsquo;s work address: a
            plausible-looking substitute is accepted here, passes validation, and then
            fails minutes later as <b>Email not verified</b> with nothing on screen to
            explain why.
          </Typography>
          <Stack spacing={2}>
            {emailDialog.rows.map((row) => (
              <Stack
                key={row.accountId}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              >
                <Typography
                  sx={{
                    minWidth: { xs: 0, sm: 220 },
                    pt: { xs: 0, sm: 1 },
                    fontFamily: FONT_BODY,
                    fontSize: '0.875rem',
                    color: CK.navy,
                    wordBreak: 'break-word',
                  }}
                >
                  {acctLabel(row.accountId)}
                </Typography>
                <TextField
                  size="small"
                  required
                  label="AWS root user email address"
                  sx={{ flex: 1, minWidth: 0 }}
                  value={emailDialog.manual[row.accountId] || ''}
                  onChange={(e) => setEmailDialog((d) => ({
                    ...d,
                    manual: { ...d.manual, [row.accountId]: e.target.value },
                  }))}
                  error={(emailDialog.manual[row.accountId] || '').trim() === ''}
                  helperText={row.reason || 'Enter this account’s AWS root user email address.'}
                />
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setEmailDialog(CLOSED_EMAIL_DIALOG)}
            sx={{ ...rowButtonSx, color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || !emailDialogComplete}
            onClick={() => submitAdd(emailDialog.accountIds, emailDialog.manual)}
            sx={detectiveButtonSx}
          >
            Invite accounts
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
